import { type ReactNode, useEffect } from "react";

import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DotsThreeVerticalIcon,
  PencilIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { startCase } from "lodash";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { z } from "zod";

import { Badge, Input, Text } from "@alpacahq/ui/old";
import {
  DataGrid,
  type TableColumn,
  type TableColumnValue,
} from "@alpacahq/ui/old/datagrid";

import { cn } from "~/v2/utils/cn";
import type { Beneficiary } from "../../../../api/rest/account";
export type LocalBeneficiary = Beneficiary & { localId: string };

const MIN_ALLOCATION = 1;

const schema = z.object({
  share_pct: z
    .number({ error: "Required" })
    .min(MIN_ALLOCATION, `Must be >= ${MIN_ALLOCATION}`)
    .max(100, "Must be <= 100"),
});

type BeneficiaryAllocationForm = z.infer<typeof schema>;

type BeneficiaryTableProps = {
  beneficiaries: LocalBeneficiary[];
  totalAllocation: number;
  onAllocationChange: (localId: string, sharePct: number) => void;
  onDelete: (localId: string) => void;
  onEdit: (localId: string) => void;
  emptyMessage?: ReactNode;
};

export const BeneficiaryTable = ({
  beneficiaries = [],
  totalAllocation,
  onAllocationChange,
  emptyMessage = "No beneficiaries added yet",
  onEdit,
  onDelete,
}: BeneficiaryTableProps) => {
  const columns: TableColumn<LocalBeneficiary>[] = [
    {
      name: "Name",
      key: "name",
      cellRenderer: ({ given_name, family_name }) => (
        <Text className="text-gray-700 text-sm">{`${given_name} ${family_name}`}</Text>
      ),
    },
    {
      name: "Relationship",
      key: "relationship",
      cellRenderer: ({ relationship }) => (
        <Badge variant="v2/blue" className="text-xs">
          {startCase(relationship)}
        </Badge>
      ),
    },
    {
      name: "Date of Birth",
      key: "dateOfBirth",
      cellRenderer: ({ date_of_birth }) => (
        <div className="text-gray-700 text-sm">{date_of_birth}</div>
      ),
    },
    {
      name: "Allocation",
      key: "allocation",
      cellRenderer: (row, value, parentRow, col) => (
        <AllocationCell
          row={row}
          value={value}
          parentRow={parentRow}
          col={col}
          // remove its own allocation from the total allocation
          totalAllocation={totalAllocation - (row.share_pct ?? 0)}
          isOverAllocated={totalAllocation > 100}
          onAllocationChange={onAllocationChange}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ),
    },
  ];

  return (
    <div className="w-full overflow-x-auto">
      <DataGrid
        columns={columns}
        rows={beneficiaries}
        emptyMessage={emptyMessage}
      />
    </div>
  );
};

const AllocationCell = ({
  row,
  totalAllocation,
  isOverAllocated,
  onAllocationChange,
  onEdit,
  onDelete,
}: {
  row: LocalBeneficiary;
  value: TableColumnValue;
  parentRow: LocalBeneficiary | null;
  col: TableColumn<LocalBeneficiary>;
  totalAllocation: number;
  isOverAllocated: boolean;
  onAllocationChange: (localId: string, sharePct: number) => void;
  onEdit: (localId: string) => void;
  onDelete: (localId: string) => void;
}) => {
  const { localId, share_pct } = row;

  const {
    watch,
    register,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<BeneficiaryAllocationForm>({
    mode: "onChange",
    resolver: zodResolver(
      schema.superRefine((data, ctx) => {
        if (data.share_pct + totalAllocation > 100) {
          ctx.addIssue({
            path: ["share_pct"],
            code: z.ZodIssueCode.custom,
            message: "Total allocation must be <= 100%",
          });
        }
      }),
    ),
    defaultValues: {
      share_pct: share_pct ?? 0,
    },
  });

  const sharePct = watch("share_pct");

  const onSubmitValid: SubmitHandler<BeneficiaryAllocationForm> = (data) => {
    onAllocationChange(localId, data.share_pct);
  };

  const onSubmitInvalid: SubmitErrorHandler<BeneficiaryAllocationForm> = () => {
    // even on error we want to send this up
    onAllocationChange(localId, sharePct);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: totalAllocation is the trigger: allocation errors are cleared when the split changes. The body only calls clearErrors.
  useEffect(() => {
    clearErrors();
  }, [totalAllocation, clearErrors]);

  return (
    <div className="flex items-center justify-end pt-4">
      <form
        onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}
        className="min-w-[80px]"
      >
        <Input
          type="number"
          className="text-sm"
          min="0"
          max="100"
          step="any"
          errorMsg={errors.share_pct?.message}
          status={
            errors.share_pct?.message || isOverAllocated ? "error" : "neutral"
          }
          rightIcon={
            <span
              className={cn(
                "text-sm",
                errors.share_pct?.message || isOverAllocated
                  ? "text-red-500"
                  : "text-gray-500",
              )}
            >
              %
            </span>
          }
          {...register("share_pct", {
            setValueAs: (value) => {
              if (value === "" || Number.isNaN(Number(value))) {
                return undefined;
              }
              return Number(value);
            },
            onChange: (e) => {
              const value = e.target.value;
              if (value === "" || Number.isNaN(Number(value))) {
                return;
              }
              onAllocationChange(localId, Number(value));
            },
          })}
          msgPadded
        />
      </form>

      <Popover className="relative pb-4">
        <PopoverButton>
          <DotsThreeVerticalIcon size={32} />
        </PopoverButton>
        <PopoverPanel
          anchor="bottom end"
          className="flex flex-col gap-1 rounded border border-gray-400 bg-white shadow-sm"
        >
          <CloseButton
            onClick={() => onEdit(localId)}
            type="button"
            className="flex items-center gap-1.5 rounded p-1.5 font-medium text-gray-700 text-sm hover:bg-cool-gray-50"
          >
            <PencilIcon size={20} /> Edit
          </CloseButton>
          <button
            onClick={() => onDelete(localId)}
            type="button"
            className="flex items-center gap-1.5 rounded p-1.5 font-medium text-gray-700 text-sm hover:bg-cool-gray-50"
          >
            <TrashIcon size={20} className="text-red-500" />
            Delete
          </button>
        </PopoverPanel>
      </Popover>
    </div>
  );
};
